/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useId, useRef, useState } from 'react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import bullet from 'highcharts/modules/bullet'
import ChartSkeleton from '../skeleton/ChartSkeleton'
import BaseChart from './BaseChart'

if (typeof bullet === 'function') {
  bullet(Highcharts)
}

interface IBulletGraphProps {
  value: number
  categories: string[]
  stops: (string | number)[][]
  textColor: string
  tickPositions?: number[]
  height?: number
  lower?: number
  upper?: number
}

const BulletGraph = (props: IBulletGraphProps) => {
  const { value, categories, stops, textColor, tickPositions, height, lower = 0, upper = 100 } = props
  const [isLoading, setIsLoading] = useState(true)
  const chartComponent = useRef<HighchartsReact.RefObject | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const chartId = useId()

  useEffect(() => {
    if (value !== undefined) {
      setIsLoading(true)
      const timer = setTimeout(() => setIsLoading(false), 400)
      return () => clearTimeout(timer)
    }
  }, [value])

  const options: any = {
    chart: {
      marginRight: 25, // Prevents text in last tick from cutting off
      inverted: true,
      type: 'bullet',
      height: height ?? 100,
      backgroundColor: 'transparent',
      style: {
        fontFamily: 'inherit',
        color: textColor,
      },
    },
    title: {
      text: null,
    },
    legend: {
      enabled: false,
    },
    yAxis: {
      gridLineWidth: 0,
      plotBands: [
        {
          color: {
            linearGradient: { x1: 0, y1: 1, x2: 1, y2: 1 },
            stops: stops,
          },
          from: lower,
          to: upper + 0.01,
        },
      ],
      title: null,
      max: upper,
      categories: categories,
      tickPositions: tickPositions ?? [lower, upper],
      labels: {
        style: {
          color: textColor,
        },
      },
    },
    xAxis: {
      visible: false,
    },
    plotOptions: {
      series: {
        pointPadding: -1,
        borderWidth: 0,
        color: 'transparent',
        targetOptions: {
          color: textColor,
        },
      },
    },

    tooltip: {
      enabled: false,
    },
    series: [
      {
        data: [
          {
            y: lower,
            target: value > lower ? value - 0.05 : value,
          },
        ],
      },
    ],
    credits: {
      enabled: false,
    },
  }

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      if (chartComponent.current) {
        chartComponent.current.chart.reflow()
      }
    })

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [])

  return (
    <>
      <div ref={containerRef} style={{ width: '100%' }}>
        {isLoading ? <ChartSkeleton fullWidth={true} /> : <BaseChart options={options} id={chartId} />}
      </div>
    </>
  )
}

export default BulletGraph
